import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import {
  ArrowLeft,
  Coins,
  Crown,
  Plus,
  Gift,
  ShoppingBag,
} from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { GooRangeSlider } from '../poker-ui/GooRangeSlider';
import { ROOM_THEME } from './LobbyTablesBlock';

const FISKI_IMG = `${process.env.PUBLIC_URL || ''}/fiski.png`;
const CASH_IMG = `${process.env.PUBLIC_URL || ''}/bucks.png`;
let shopRewardAudioContext = null;

function formatNumber(value) {
  return Number(value || 0).toLocaleString('ru-RU');
}

function formatCash(value) {
  return Number(value || 0).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function createCoinParticles(count = 22) {
  return Array.from({ length: count }).map((_, idx) => ({
    id: `${Date.now()}-${idx}`,
    left: Math.random() * 100,
    delay: Math.random() * 0.42,
    duration: 1.05 + Math.random() * 0.85,
    drift: -52 + Math.random() * 104,
    rotateStart: Math.random() * 140,
    rotateEnd: 540 + Math.random() * 560,
    scale: 0.75 + Math.random() * 0.55,
    opacity: 0.58 + Math.random() * 0.35,
  }));
}

function playDailyRewardSound(power = 1) {
  try {
    if (typeof window === 'undefined') return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;

    if (!shopRewardAudioContext || shopRewardAudioContext.state === 'closed') {
      shopRewardAudioContext = new AC();
    }
    if (shopRewardAudioContext.state === 'suspended') {
      shopRewardAudioContext.resume();
    }

    const ctx = shopRewardAudioContext;
    const start = ctx.currentTime + 0.01;
    const intensity = Math.max(0.7, Math.min(1.5, power));

    const makeTone = (freq, duration, timeOffset, gain, type = 'sine') => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, start + timeOffset);
      g.gain.setValueAtTime(Math.max(0.0001, gain), start + timeOffset);
      g.gain.exponentialRampToValueAtTime(0.0001, start + timeOffset + duration);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(start + timeOffset);
      osc.stop(start + timeOffset + duration);
    };

    makeTone(640 * intensity, 0.09, 0, 0.1, 'triangle');
    makeTone(920 * intensity, 0.11, 0.08, 0.085, 'sine');
    makeTone(1220 * intensity, 0.2, 0.16, 0.08, 'sine');
    makeTone(1780 * intensity, 0.04, 0.07, 0.04, 'square');
    makeTone(1960 * intensity, 0.03, 0.14, 0.03, 'square');
  } catch {
    // ignore audio errors
  }
}

function CoinRainOverlay({ burst }) {
  if (!burst) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[140] overflow-hidden" aria-hidden>
      {burst.particles.map((p) => (
        <div
          key={p.id}
          className="absolute top-[-10%] animate-[shop-coin-fall_linear_forwards]"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--coin-drift': `${p.drift}px`,
            '--coin-rot-start': `${p.rotateStart}deg`,
            '--coin-rot-end': `${p.rotateEnd}deg`,
          }}
        >
          <img
            src={FISKI_IMG}
            alt=""
            width={24}
            height={24}
            className="h-6 w-6 object-contain drop-shadow-[0_3px_8px_rgba(0,0,0,0.45)]"
            style={{ opacity: p.opacity, transform: `scale(${p.scale})` }}
            draggable={false}
          />
        </div>
      ))}
    </div>
  );
}

function createCashFlightParticles(fromRect, toRect, count = 7) {
  const fromX = fromRect.left + fromRect.width / 2;
  const fromY = fromRect.top + fromRect.height / 2;
  const toX = toRect.left + toRect.width / 2;
  const toY = toRect.top + toRect.height / 2;

  return Array.from({ length: count }).map((_, idx) => ({
    id: `${Date.now()}-${idx}`,
    fromX,
    fromY,
    toX: toX + (-10 + Math.random() * 20),
    toY: toY + (-8 + Math.random() * 16),
    delay: idx * 0.05,
    duration: 0.62 + Math.random() * 0.24,
    rotateStart: Math.random() * 120,
    rotateEnd: 220 + Math.random() * 240,
    scale: 0.78 + Math.random() * 0.44,
  }));
}

function CashFlightOverlay({ burst }) {
  if (!burst) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[145] overflow-hidden" aria-hidden>
      {burst.particles.map((p) => (
        <div
          key={p.id}
          className="absolute animate-[shop-cash-flight_ease-out_forwards]"
          style={{
            left: `${p.fromX}px`,
            top: `${p.fromY}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--cash-to-x': `${p.toX - p.fromX}px`,
            '--cash-to-y': `${p.toY - p.fromY}px`,
            '--cash-rot-start': `${p.rotateStart}deg`,
            '--cash-rot-end': `${p.rotateEnd}deg`,
            '--cash-scale': p.scale,
          }}
        >
          <img
            src={CASH_IMG}
            alt=""
            width={22}
            height={22}
            className="h-[22px] w-[22px] object-contain drop-shadow-[0_3px_8px_rgba(0,0,0,0.42)]"
            draggable={false}
          />
        </div>
      ))}
    </div>
  );
}

function useSoundEffects() {
  const audioContextRef = useRef(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AC();
    }
    return audioContextRef.current;
  }, []);

  const playCoinsSound = useCallback(() => {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    [0, 0.08, 0.16, 0.24].forEach((delay, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 760 + i * 120;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.14, now + delay);
      gain.gain.setTargetAtTime(0.01, now + delay, 0.05);
      osc.start(now + delay);
      osc.stop(now + delay + 0.13);
    });
  }, [getAudioContext]);

  const playCashSound = useCallback(() => {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Warm "cash received" chime: soft attack, short musical tail.
    const playTone = (freq, delay, duration, volume, type = 'triangle') => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, now + delay);
      gain.gain.setValueAtTime(0.0001, now + delay);
      gain.gain.exponentialRampToValueAtTime(volume, now + delay + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + duration);
      osc.start(now + delay);
      osc.stop(now + delay + duration + 0.02);
    };

    // Gentle body so the sound feels "valuable", not harsh.
    playTone(165, 0, 0.24, 0.03, 'sine');
    // Main cash-register-like sparkle.
    playTone(587.33, 0.025, 0.2, 0.075, 'triangle');
    playTone(783.99, 0.105, 0.24, 0.065, 'sine');
  }, [getAudioContext]);

  const playVipSound = useCallback(() => {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'triangle';
      gain.gain.setValueAtTime(0.11, now + i * 0.06);
      gain.gain.setTargetAtTime(0.01, now + 0.28 + i * 0.06, 0.18);
      osc.start(now + i * 0.06);
      osc.stop(now + 0.62);
    });
  }, [getAudioContext]);

  const playClickSound = useCallback(() => {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 580;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.07, now);
    gain.gain.setTargetAtTime(0, now, 0.02);
    osc.start(now);
    osc.stop(now + 0.04);
  }, [getAudioContext]);

  return { playCoinsSound, playCashSound, playVipSound, playClickSound };
}

const SHOP_CATEGORIES = [
  { id: 'coins', label: 'Монеты и валюта', icon: Coins },
  { id: 'free', label: 'Бесплатные награды', icon: Gift },
  { id: 'vip', label: 'VIP', icon: Crown },
];

const CORE_SHOP_TAB_IDS = new Set(['coins', 'free', 'vip']);

function isCoreShopTab(tabId) {
  return CORE_SHOP_TAB_IDS.has(tabId);
}

/** Вкладки: три базовых + id кастомных разделов с сервера (id не должен совпадать с coins/free/vip). */
function buildShopTabs(shopSections) {
  const core = SHOP_CATEGORIES.map((c) => ({ id: c.id, label: c.label, icon: c.icon }));
  const extra = (shopSections || []).map((s) => ({
    id: s.id,
    label: s.title,
    icon: ShoppingBag,
  }));
  return [...core, ...extra];
}

const COIN_PACKAGES = [
  { id: 'coins-1', coins: 3500, price: 0.99, bonus: 0, vipPoints: 690 },
  { id: 'coins-2', coins: 42000, price: 1.99, bonus: 25, vipPoints: 1530 },
  { id: 'coins-3', coins: 110000, price: 4.99, bonus: 35, vipPoints: 3520 },
  { id: 'coins-4', coins: 235000, price: 9.99, bonus: 45, vipPoints: 8080 },
  { id: 'coins-5', coins: 540000, price: 19.99, bonus: 70, vipPoints: 23500, label: 'Популярно' },
  { id: 'coins-6', coins: 4200000, price: 99.99, bonus: 90, vipPoints: 55000, label: 'Лучшее предложение' },
];

const CASH_PACKAGES = [
  { id: 'cash-1', cash: 5, price: 5, bonus: 0, vipPoints: 500 },
  { id: 'cash-2', cash: 10, price: 10, bonus: 0, vipPoints: 1000, label: 'Популярно' },
  { id: 'cash-3', cash: 25, price: 25, bonus: 0, vipPoints: 2500 },
  { id: 'cash-4', cash: 50, price: 50, bonus: 0, vipPoints: 5000 },
  { id: 'cash-5', cash: 100, price: 100, bonus: 0, vipPoints: 10000 },
  { id: 'cash-6', cash: 200, price: 200, bonus: 0, vipPoints: 20000, label: 'Лучшее предложение' },
];

const FREE_REWARDS = [
  { id: 'free-1', coins: 60, type: 'daily', claimIn: '2ч 34м', icon: Coins, claimReady: false },
  { id: 'free-2', coins: 5000, type: 'hourly', claimReady: true, icon: Coins },
  { id: 'free-3', coins: 7500, type: 'hourly', claimReady: true, icon: Coins },
  { id: 'free-4', coins: 10000, type: 'hourly', claimReady: true, icon: Coins },
  { id: 'free-5', name: 'Видеобокс', type: 'video', claimReady: true, icon: Gift },
];

const VIP_PACKAGES = [
  {
    id: 'vip-week',
    name: 'VIP Неделя',
    duration: '7 дней',
    durationDays: 7,
    perks: ['+50% XP', '+25% выигрыша', 'Без рекламы', 'VIP значок'],
    price: 2.99,
    popular: false,
  },
  {
    id: 'vip-month',
    name: 'VIP Месяц',
    duration: '30 дней',
    durationDays: 30,
    perks: ['+100% XP', '+50% выигрыша', 'Без рекламы', 'VIP значок', 'Эксклюзивные столы'],
    price: 7.99,
    oldPrice: 11.96,
    popular: true,
  },
  {
    id: 'vip-year',
    name: 'VIP Год',
    duration: '365 дней',
    durationDays: 365,
    perks: ['+150% XP', '+75% выигрыша', 'Без рекламы', 'Легендарный значок', 'Все эксклюзивы'],
    price: 49.99,
    oldPrice: 95.88,
    popular: false,
  },
];

function promotionIsShown(p) {
  if (!p) return false;
  if (typeof p.effectiveActive === 'boolean') return p.effectiveActive;
  return Boolean(p.active);
}

function normalizeCatalog(catalog) {
  if (!catalog || typeof catalog !== 'object') {
    return {
      coinPackages: COIN_PACKAGES,
      cashPackages: CASH_PACKAGES,
      vipPackages: VIP_PACKAGES,
      promotions: [],
      shopSections: [],
    };
  }
  return {
    coinPackages: Array.isArray(catalog.coinPackages) && catalog.coinPackages.length > 0 ? catalog.coinPackages : COIN_PACKAGES,
    cashPackages: Array.isArray(catalog.cashPackages) && catalog.cashPackages.length > 0 ? catalog.cashPackages : CASH_PACKAGES,
    vipPackages: Array.isArray(catalog.vipPackages) && catalog.vipPackages.length > 0 ? catalog.vipPackages : VIP_PACKAGES,
    promotions: Array.isArray(catalog.promotions) ? catalog.promotions : [],
    shopSections: Array.isArray(catalog.shopSections) ? catalog.shopSections : [],
  };
}

function BalanceDisplay({ cash = 0, coins = 0, animating, cashIconRef, cashValueRef }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={cn(
          'flex max-w-[8.4rem] items-center rounded-full border border-emerald-500/30 bg-gradient-to-r from-[#1a2f1a] to-[#0f1f0f] px-1 py-0.5',
          animating === 'cash' && 'animate-[shop-pop_380ms_ease-out]'
        )}
      >
        <div ref={cashIconRef} className="flex h-6 w-6 items-center justify-center">
          <img src={CASH_IMG} alt="" className="h-4 w-4 object-contain" draggable={false} />
        </div>
        <span
          ref={cashValueRef}
          className={cn(
            'truncate px-1.5 text-xs font-bold tabular-nums text-white',
            animating === 'cash' && 'animate-[shop-cash-value-pop_420ms_ease-out]'
          )}
        >
          {formatNumber(cash)}
        </span>
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-b from-green-400 to-green-600 text-white"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={3} />
        </button>
      </div>

      <div
        className={cn(
          'flex max-w-[9.2rem] items-center rounded-full border border-amber-500/30 bg-gradient-to-r from-[#2f2a1a] to-[#1f1a0f] px-1 py-0.5',
          animating === 'coins' && 'animate-[shop-pop_380ms_ease-out]'
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center">
          <img src={FISKI_IMG} alt="" className="h-4 w-4 object-contain" draggable={false} />
        </div>
        <span className="truncate px-1.5 text-xs font-bold tabular-nums text-white">{formatNumber(coins)}</span>
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-b from-green-400 to-green-600 text-white"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

function CoinCard({ pkg, onBuy }) {
  return (
    <article
      className={cn(
        'relative flex flex-col overflow-hidden rounded-xl border border-[#2a3040]/60 bg-gradient-to-b from-[#1a1f25] to-[#0f1215] transition-all duration-200 hover:scale-[1.02] hover:border-amber-400/50 active:scale-[0.98]',
        pkg.label && 'border-amber-400/60'
      )}
    >
      {pkg.bonus > 0 ? <div className="absolute -right-8 top-3 z-10 rotate-45 bg-gradient-to-r from-red-600 to-red-500 px-8 py-0.5 text-[10px] font-black text-white">+{pkg.bonus}%</div> : null}
      {pkg.label ? <div className="absolute left-0 right-0 top-0 bg-gradient-to-r from-red-600 via-red-500 to-red-600 py-1 text-center text-[10px] font-bold uppercase text-white">{pkg.label}</div> : null}
      <div className={cn('flex items-center justify-center py-4', pkg.label && 'pt-8')}>
        <img src={FISKI_IMG} alt="" className="h-20 w-20 object-contain drop-shadow-[0_4px_12px_rgba(251,191,36,0.4)]" draggable={false} />
      </div>
      <div className="px-2 pb-1 text-center">
        <p className="text-xl font-black text-white">{formatNumber(pkg.coins)}</p>
        <p className="text-[10px] text-slate-300">+ VIP-очки ({formatNumber(pkg.vipPoints)})</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onBuy?.(e);
        }}
        className="m-2 mt-1 rounded-lg border border-green-400/50 bg-gradient-to-b from-green-500 to-green-700 py-2 text-base font-black text-white shadow-[0_4px_0_#166534]"
      >
        {'\u20AC'}
        {pkg.price}
      </button>
    </article>
  );
}

function CashCard({ pkg, onBuy }) {
  return (
    <article className="relative flex flex-col overflow-hidden rounded-xl border border-[#2a3040]/60 bg-gradient-to-b from-[#1a1f25] to-[#0f1215] transition-all duration-200 hover:scale-[1.02] hover:border-emerald-400/50 active:scale-[0.98]">
      {pkg.bonus > 0 ? <div className="absolute -right-8 top-3 z-10 rotate-45 bg-gradient-to-r from-red-600 to-red-500 px-8 py-0.5 text-[10px] font-black text-white">+{pkg.bonus}%</div> : null}
      {pkg.label ? <div className="absolute left-0 right-0 top-0 bg-gradient-to-r from-red-600 via-red-500 to-red-600 py-1 text-center text-[10px] font-bold uppercase text-white">{pkg.label}</div> : null}
      <div className={cn('flex items-center justify-center py-4', pkg.label && 'pt-8')}>
        <img src={CASH_IMG} alt="" className="h-20 w-20 object-contain drop-shadow-[0_4px_12px_rgba(16,185,129,0.4)]" draggable={false} />
      </div>
      <div className="px-2 pb-1 text-center">
        <p className="text-xl font-black text-white">{formatCash(pkg.cash)}</p>
        <p className="text-[10px] text-slate-300">+ VIP-очки ({formatNumber(pkg.vipPoints)})</p>
      </div>
      <button
        type="button"
        onClick={(e) => onBuy?.(e)}
        className="m-2 mt-1 rounded-lg border border-green-400/50 bg-gradient-to-b from-green-500 to-green-700 py-2 text-base font-black text-white shadow-[0_4px_0_#166534]"
      >
        {'\u20AC'}{formatCash(pkg.price)}
      </button>
    </article>
  );
}

function CashSliderCard({ value, onChange, onBuy }) {
  return (
    <article className="rounded-xl border border-emerald-400/35 bg-gradient-to-b from-[#15271a] to-[#0d1610] p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-black uppercase tracking-wide text-emerald-200">Гибкий пакет</p>
        <span className="rounded-md border border-emerald-300/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-100">
          Stripe
        </span>
      </div>

      <div className="mb-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
        <p className="text-xs text-slate-300">Вы получите</p>
        <p className="text-xl font-black tabular-nums text-emerald-100">{formatCash(value)} кэша</p>
        <p className="text-xs text-slate-400">Стоимость: {'\u20AC'}{formatCash(value)} (1:1)</p>
      </div>

      <GooRangeSlider
        min={5}
        max={200}
        step={0.5}
        value={value}
        onChange={onChange}
        fill={ROOM_THEME.poker.sliderFill}
        {...ROOM_THEME.poker.gooSlider}
        dense
        showValue={false}
        formatValue={(v) => formatCash(v)}
        ariaLabel="Выбор суммы кэша"
      />

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => onBuy?.(e)}
          className="flex-1 rounded-lg border border-green-400/50 bg-gradient-to-b from-green-500 to-green-700 py-2 text-sm font-black text-white shadow-[0_4px_0_#166534]"
        >
          Купить за {'\u20AC'}{formatCash(value)}
        </button>
      </div>
    </article>
  );
}

function FreeRewardCard({ reward, onClaim }) {
  const Icon = reward.icon;
  return (
    <article className="flex items-center gap-3 rounded-xl border border-[#2a3040]/60 bg-gradient-to-r from-[#1a1f25] to-[#0f1215] p-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10">
        <Icon className="h-7 w-7 text-amber-400" />
      </div>
      <div className="flex-1">
        {reward.coins ? <p className="text-lg font-black text-white">{formatNumber(reward.coins)}</p> : null}
        {reward.name ? <p className="text-base font-bold text-white">{reward.name}</p> : null}
        {reward.claimIn ? <p className="text-xs text-slate-400">Забрать через {reward.claimIn}</p> : null}
      </div>
      <button type="button" onClick={onClaim} disabled={!reward.claimReady} className={cn('flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-bold transition-all', reward.claimReady ? 'border-green-400/50 bg-gradient-to-b from-green-500 to-green-700 text-white shadow-[0_3px_0_#166534]' : 'cursor-not-allowed border-slate-600/30 bg-slate-700/50 text-slate-400')}>
        Забрать
        {reward.type === 'video' ? <span className="text-[10px]">AD</span> : null}
      </button>
    </article>
  );
}

const CustomItemCard = memo(function CustomItemCard({ item, onBuy }) {
  const [imgOk, setImgOk] = useState(Boolean(item.imageUrl));
  useEffect(() => {
    setImgOk(Boolean(item.imageUrl));
  }, [item.imageUrl]);
  const showImg = Boolean(item.imageUrl) && imgOk;
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#151b26] via-[#0f131c] to-[#090c11] shadow-[0_14px_42px_rgba(0,0,0,0.38)] transition duration-300 hover:-translate-y-0.5 hover:border-violet-400/40 hover:shadow-[0_18px_50px_rgba(124,58,237,0.14)]">
      {item.badge ? (
        <span className="absolute left-3 top-3 z-10 rounded-md border border-fuchsia-400/45 bg-fuchsia-950/90 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-fuchsia-100 shadow-sm">
          {item.badge}
        </span>
      ) : null}
      <div className="relative aspect-[5/4] w-full overflow-hidden bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(139,92,246,0.18),transparent_60%)]">
        {showImg ? (
          <img
            src={item.imageUrl}
            alt=""
            className="h-full w-full object-cover transition duration-500 ease-out group-hover:scale-[1.04]"
            loading="lazy"
            draggable={false}
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-slate-500">
            <ShoppingBag className="h-12 w-12 opacity-45" strokeWidth={1.25} aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">Без изображения</span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#090c11] via-[#090c11]/80 to-transparent" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <h3 className="text-[15px] font-black leading-snug tracking-tight text-white">{item.name}</h3>
        {item.description ? (
          <p className="line-clamp-3 text-xs leading-relaxed text-slate-400">{item.description}</p>
        ) : null}
        <div className="mt-auto flex flex-wrap items-end justify-between gap-2 border-t border-white/5 pt-3">
          <div className="min-w-0">
            {item.oldPrice != null && Number(item.oldPrice) > 0 ? (
              <p className="text-xs text-slate-500 line-through">{'\u20AC'}{item.oldPrice}</p>
            ) : null}
            <p className="text-lg font-black tabular-nums text-emerald-100">{'\u20AC'}{item.price}</p>
            {Number(item.grantCoins) > 0 ? (
              <p className="text-[10px] font-bold text-amber-200/90">+{formatNumber(item.grantCoins)} монет</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onBuy}
            className="shrink-0 rounded-xl border border-emerald-400/55 bg-gradient-to-b from-emerald-500 to-emerald-700 px-4 py-2 text-xs font-black uppercase tracking-wide text-white shadow-[0_4px_0_#065f46] transition hover:brightness-110 active:translate-y-px active:shadow-none"
          >
            Купить
          </button>
        </div>
      </div>
    </article>
  );
});

function VipCard({ vip, onBuy }) {
  return (
    <article
      className={cn(
        'relative overflow-hidden rounded-xl border p-4 transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]',
        'bg-gradient-to-b from-[#1a1f25] to-[#0f1215]',
        vip.popular ? 'border-amber-400/60 animate-[shop-glow_2s_ease-in-out_infinite]' : 'border-[#2a5080]/60'
      )}
    >
      {vip.popular ? <div className="absolute -right-8 top-4 rotate-45 bg-gradient-to-r from-amber-500 to-orange-500 px-10 py-0.5 text-[9px] font-black uppercase text-white">Лучшее</div> : null}
      <div className="flex items-center gap-4">
        <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/30">
          <Crown className="h-8 w-8 text-amber-400 drop-shadow-lg" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-black text-white">{vip.name}</h3>
          <p className="text-sm text-slate-400">{vip.duration}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {vip.perks.slice(0, 3).map((perk, idx) => (
              <span key={`${vip.id}-${idx}`} className="text-[10px] text-emerald-400">{perk}</span>
            ))}
          </div>
        </div>
        <div className="text-right">
          {vip.oldPrice ? <p className="text-sm text-slate-500 line-through">{'\u20AC'}{vip.oldPrice}</p> : null}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onBuy?.(e);
            }}
            className="rounded-lg border border-green-400/50 bg-gradient-to-b from-green-500 to-green-700 px-4 py-2 text-base font-black text-white shadow-[0_4px_0_#166534]"
          >
            {'\u20AC'}
            {vip.price}
          </button>
        </div>
      </div>
    </article>
  );
}

function CategoryTabs({ tabs, active, onChange }) {
  return (
    <nav
      className="shrink-0 border-t border-white/10 bg-[#05080e]/95 backdrop-blur-md"
      aria-label="Разделы магазина"
    >
      <div className="flex gap-1.5 overflow-x-auto px-3 py-2.5 [scrollbar-width:thin] sm:px-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isOn = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-left text-xs font-bold transition-all duration-200 sm:text-[13px]',
                isOn
                  ? 'bg-gradient-to-r from-amber-500/35 via-orange-500/25 to-amber-600/30 text-amber-50 shadow-[0_0_24px_rgba(251,191,36,0.2)] ring-1 ring-amber-400/55'
                  : 'bg-white/[0.07] text-slate-400 hover:bg-white/12 hover:text-slate-100 active:scale-[0.98]'
              )}
            >
              <Icon className={cn('h-4 w-4 shrink-0 sm:h-[18px] sm:w-[18px]', isOn ? 'text-amber-200' : 'text-slate-500')} />
              <span className="max-w-[9rem] truncate sm:max-w-[12rem]">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function ShopCustomSectionPane({ section, onBuyItem }) {
  if (!section) return null;
  const items = section.items || [];
  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-violet-300/90">Каталог</p>
        <h2 className="text-xl font-black tracking-tight text-white sm:text-2xl">{section.title}</h2>
        {section.subtitle ? <p className="max-w-2xl text-sm leading-relaxed text-slate-400">{section.subtitle}</p> : null}
      </header>
      {items.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {items.map((it) => (
            <CustomItemCard
              key={it.id}
              item={it}
              onBuy={() => onBuyItem({ ...it, kind: 'shopCustom', sectionId: section.id })}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-6 text-center text-sm text-slate-500">
          В этом разделе пока нет товаров.
        </p>
      )}
    </section>
  );
}

export function LobbyShopScreen({
  onBack,
  onPurchase,
  userBalance,
  userCash,
  catalog,
  /** false = только просмотр каталога, без списаний (флаг shopPurchasesEnabled). */
  purchasesEnabled = true,
  /** true, если на сервере задан STRIPE_SECRET_KEY (см. GET /api/config/public). */
  stripeCheckoutConfigured = false,
  /** Показать предупреждение: Stripe готов, но нет JWT (нужен вход в аккаунт). */
  checkoutBlockedNoSession = false,
  /** Вызывается при нажатии «Купить», если покупки отключены (вместо тихого return). */
  onViewOnlyPurchaseAttempt,
}) {
  const [category, setCategory] = useState('coins');
  const [balanceAnimating, setBalanceAnimating] = useState(null);
  const [coinBurst, setCoinBurst] = useState(null);
  const [cashFlightBurst, setCashFlightBurst] = useState(null);
  const [cashSliderValue, setCashSliderValue] = useState(25);
  const burstTimeoutRef = useRef(null);
  const cashFlightTimeoutRef = useRef(null);
  const cashIconRef = useRef(null);
  const cashValueRef = useRef(null);
  const { playCashSound, playVipSound, playClickSound } = useSoundEffects();
  const data = normalizeCatalog(catalog);
  const shopTabs = buildShopTabs(data.shopSections);
  const customSection =
    !isCoreShopTab(category) ? (data.shopSections || []).find((s) => s.id === category) ?? null : null;

  useEffect(() => {
    if (isCoreShopTab(category)) return;
    if (!(data.shopSections || []).some((s) => s.id === category)) setCategory('coins');
  }, [category, data.shopSections]);

  const handleBuy = useCallback((item, type, sourceEl) => {
    if (!purchasesEnabled) {
      onViewOnlyPurchaseAttempt?.();
      return;
    }
    const animType = type || 'coins';
    playClickSound();

    if (animType === 'custom') {
      onPurchase?.(item);
      return;
    }

    if (animType === 'coins') {
      const coinAmount = Number(item?.coins) || 5000;
      const particleCount = Math.min(36, 16 + Math.floor(coinAmount / 350));
      setCoinBurst({
        id: `${Date.now()}-${item?.id || 'coins'}`,
        particles: createCoinParticles(particleCount),
      });
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
      burstTimeoutRef.current = setTimeout(() => setCoinBurst(null), 2100);
      playDailyRewardSound(0.9 + coinAmount / 7000);
    }

    setTimeout(() => {
      setBalanceAnimating(animType);
      if (animType === 'cash') playCashSound();
      else if (animType === 'vip') playVipSound();
    }, 280);

    if (animType === 'cash' && sourceEl && cashIconRef.current) {
      const fromRect = sourceEl.getBoundingClientRect();
      const toRect = cashIconRef.current.getBoundingClientRect();
      setCashFlightBurst({
        id: `${Date.now()}-${item?.id || 'cash'}`,
        particles: createCashFlightParticles(fromRect, toRect),
      });
      if (cashFlightTimeoutRef.current) clearTimeout(cashFlightTimeoutRef.current);
      cashFlightTimeoutRef.current = setTimeout(() => setCashFlightBurst(null), 1400);
    }
    setTimeout(() => setBalanceAnimating(null), 1020);
    onPurchase?.(item);
  }, [onPurchase, onViewOnlyPurchaseAttempt, playCashSound, playClickSound, playVipSound, purchasesEnabled]);

  useEffect(() => {
    return () => {
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
      if (cashFlightTimeoutRef.current) clearTimeout(cashFlightTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[85] flex h-screen w-full flex-col overflow-hidden bg-[#05080e] text-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shop-title"
    >
      <style>{`
        @keyframes shop-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(251, 191, 36, 0.3); }
          50% { box-shadow: 0 0 30px rgba(251, 191, 36, 0.5); }
        }
        @keyframes shop-pop {
          0% { transform: scale(1); }
          30% { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        @keyframes shop-coin-fall {
          0% {
            transform: translate3d(0, -8vh, 0) rotate(var(--coin-rot-start)) scale(0.85);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translate3d(var(--coin-drift), 108vh, 0) rotate(var(--coin-rot-end)) scale(1.08);
            opacity: 0;
          }
        }
        @keyframes shop-cash-flight {
          0% {
            transform: translate(-50%, -50%) translate3d(0,0,0) rotate(var(--cash-rot-start)) scale(var(--cash-scale));
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) translate3d(var(--cash-to-x), var(--cash-to-y), 0) rotate(var(--cash-rot-end)) scale(0.6);
            opacity: 0;
          }
        }
        @keyframes shop-cash-value-pop {
          0% { transform: scale(1); filter: brightness(1); }
          35% { transform: scale(1.18); filter: brightness(1.25); }
          100% { transform: scale(1); filter: brightness(1); }
        }
      `}</style>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0f1419] via-[#0a0d12] to-[#050709]" />
      <CoinRainOverlay burst={coinBurst} />
      <CashFlightOverlay burst={cashFlightBurst} />

      <header className="relative z-10 flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2.5 pt-[max(0.75rem,env(safe-area-inset-top,0px))] sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={onBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-b from-red-500 to-red-700 text-white shadow-[0_3px_0_#991b1b]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 id="shop-title" className="truncate text-xl font-black text-white sm:text-2xl">Магазин</h1>
        </div>
        <div className="shrink-0">
          <BalanceDisplay
            coins={userBalance ?? 0}
            cash={userCash ?? 0}
            animating={balanceAnimating}
            cashIconRef={cashIconRef}
            cashValueRef={cashValueRef}
          />
        </div>
      </header>

      {!purchasesEnabled ? (
        <div className="relative z-10 border-b border-amber-400/30 bg-amber-950/40 px-4 py-2 text-center text-xs font-medium text-amber-100/95">
          Каталог для ознакомления: реальные покупки и списание отключены. Включение — в админке (флаг shopPurchasesEnabled).
        </div>
      ) : null}
      {purchasesEnabled && !stripeCheckoutConfigured ? (
        <div className="relative z-10 border-b border-sky-500/30 bg-sky-950/35 px-4 py-2 text-center text-xs font-medium text-sky-100/95">
          Stripe не подключён: в корне сервера создайте <span className="font-mono text-[11px]">server/.env</span> (образец{' '}
          <span className="font-mono text-[11px]">server/.env.example</span>), вставьте{' '}
          <span className="font-mono text-[11px]">STRIPE_SECRET_KEY</span> (sk_test_… из Stripe Dashboard → Test keys) и{' '}
          <span className="font-mono text-[11px]">STRIPE_WEBHOOK_SECRET</span> (локально: в папке server —{' '}
          <span className="font-mono text-[11px]">npm run stripe:listen</span>
          ), затем перезапустите сервер.
        </div>
      ) : null}
      {checkoutBlockedNoSession ? (
        <div className="relative z-10 border-b border-rose-500/35 bg-rose-950/45 px-4 py-2.5 text-center text-xs font-medium text-rose-50/95">
          Чтобы открылась оплата Stripe, войдите в аккаунт (меню «Профиль» / регистрация). В демо-режиме разработки без
          входа JWT не выдаётся — кнопка «Купить» не перенаправит на Stripe.
        </div>
      ) : null}

      <main className="relative z-10 flex-1 overflow-y-auto px-4 pb-4">
        {category === 'coins' ? (
          <section className="space-y-6">
            {data.promotions.some((p) => promotionIsShown(p)) ? (
              <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-2 text-xs text-amber-100">
                Акции:{' '}
                {data.promotions
                  .filter((p) => promotionIsShown(p))
                  .map((p) => p.title || p.id)
                  .join(', ')}
              </div>
            ) : null}
            <div>
              <h2 className="mb-4 text-lg font-black text-white">Наборы монет</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {data.coinPackages.map((pkg) => (
                  <CoinCard key={pkg.id} pkg={pkg} onBuy={() => handleBuy(pkg, 'coins')} />
                ))}
              </div>
            </div>
            <div>
              <h2 className="mb-2 text-lg font-black text-white">Наборы кэша (1 кэш = 1 евро)</h2>
              <p className="mb-4 text-xs text-slate-400">Покупка на {'\u20AC'}10,00 даёт 10,00 кэша</p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {data.cashPackages.map((pkg) => (
                  <CashCard
                    key={pkg.id}
                    pkg={pkg}
                    onBuy={(e) => handleBuy(pkg, 'cash', e.currentTarget)}
                  />
                ))}
              </div>
              <div className="mt-4">
                <CashSliderCard
                  value={cashSliderValue}
                  onChange={setCashSliderValue}
                  onBuy={(e) =>
                    handleBuy(
                      {
                        id: 'cash-custom',
                        name: 'Гибкий пакет кэша',
                        cash: cashSliderValue,
                        price: cashSliderValue,
                      },
                      'cash',
                      e.currentTarget
                    )
                  }
                />
              </div>
            </div>
          </section>
        ) : null}

        {category === 'free' ? (
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-black text-white">Бесплатные монеты</h2>
              <span className="text-xs text-slate-400">Сброс через 7ч 11м</span>
            </div>
            <div className="space-y-3">
              {FREE_REWARDS.map((reward) => (
                <FreeRewardCard key={reward.id} reward={reward} onClaim={() => handleBuy(reward, 'coins')} />
              ))}
            </div>
          </section>
        ) : null}

        {category === 'vip' ? (
          <section>
            <h2 className="mb-4 text-lg font-black text-white">VIP подписки</h2>
            <div className="space-y-3">
              {data.vipPackages.map((vip) => (
                <VipCard key={vip.id} vip={vip} onBuy={() => handleBuy(vip, 'vip')} />
              ))}
            </div>
          </section>
        ) : null}

        {customSection ? (
          <ShopCustomSectionPane section={customSection} onBuyItem={(payload) => handleBuy(payload, 'custom')} />
        ) : null}
      </main>

      <CategoryTabs tabs={shopTabs} active={category} onChange={setCategory} />
    </div>
  );
}

export default LobbyShopScreen;
